"use client";

import ProductPage from "@/components/ProductPage";

import titelbild from "./crmtitel.png";
import ImageHighlight from "./highlight.jpg";
import ImageFeaturePOS from "./feature-pos.jpg";
import ImageFeatureCustomers from "./feature-customers.jpg";
import ImageFeatureStock from "./feature-stock.jpg";
import ImageFeatureStats from "./feature-stats.jpg";
import ImageFeatureOrder from "./feature-order.jpg";
import ImageFeatureExport from "./feature-export.jpg";
import { Check } from "lucide-react";
import ImageText from "@/components/ImageText";
import Section from "@/components/Section";


export default function Page() {
  return (
    <ProductPage
      productName="CRM4Trade – CRM-Software für den Handel"
      hero={{
        title: "CRM4Trade – CRM-Software für den Handel",
        subtitle:
          "Strukturieren Sie Kundenbeziehungen, Vertriebsprozesse und Aktivitäten zentral – inklusive Historie, Aufgaben, Auswertungen und Schnittstellen.",
        buttonLeft: {
          title: "Unverbindliche Anfrage",
          href: "/support",
        },
        buttonRight: { title: "Preise", href: "/preise" },
        image: titelbild,
        imageAlt: "CRM4Trade",
      }}
      highlight={{
        image: ImageHighlight,
        bullets: [
          <>
            <strong>Zentrale Kundenakte</strong> mit Historie, Notizen,
            Kontakten und Dokumenten.
          </>,
          <>
            <strong>Vertrieb & Pipeline</strong> für Angebote, Leads und
            Aufgaben – alles im Blick.
          </>,
          <>
            <strong>Auswertungen & Reports</strong> für Aktivitäten,
            Umsatzpotenziale und Teamleistung.
          </>,
          <>
            <strong>Schnittstellenfähig</strong> zu Warenwirtschaft, Buchhaltung
            und Online-Systemen.
          </>,
        ],
      }}
      beforeVideo={
        <Section variant="white" className="py-12 md:py-20">
          <ImageText
            imagePosition="right"
            imageSlot={
              <div className="relative overflow-hidden mt-12 rounded-2xl border border-black/10 bg-black shadow-sm h-100">
                <video className="h-full w-full object-cover" controls>
                  <source src="/VideoFürWebseite.mp4" type="video/mp4" />
                </video>
              </div>
            }
            title="ModeProfi – ERP- und Warenwirtschaftssystem für den Mode- und Textilgroßhandel"
          >
            <p className="text-slate-700 leading-relaxed">
              CRM4Trade ist die CRM-Lösung speziell für Unternehmen im Handel.
              Verwalten Sie Kundenbeziehungen, Vertriebsprozesse und Aktivitäten
              zentral in einem System – von der ersten Kontaktaufnahme über
              Angebote und Aufgaben bis zur langfristigen Kundenbetreuung.
            </p>
            <p className="mt-4 text-slate-700 leading-relaxed">
              Mit einer zentralen Kundenakte, strukturierten Vertriebsprozessen
              und aussagekräftigen Auswertungen behalten Sie jederzeit den
              Überblick über Leads, Umsatzpotenziale und Teamaktivitäten. Dank
              moderner Schnittstellen lässt sich CRM4Trade nahtlos mit
              Warenwirtschaft, Buchhaltung und Online-Systemen verbinden.
            </p>

            <ul className="mt-4 space-y-2">
              <li className="flex items-start gap-3 text-slate-700">
                <Check className="shrink-0 mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Zentrale Kundenakte:</strong> Alle Kontakte, Notizen,
                  Dokumente und Aktivitäten übersichtlich an einem Ort.
                </span>
              </li>
              <li className="flex items-start gap-3 text-slate-700">
                <Check className="shrink-0 mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Vertrieb im Überblick:</strong> Pipeline für Leads,
                  Angebote und Aufgaben zur strukturierten Steuerung Ihres
                  Vertriebs.
                </span>
              </li>
              <li className="flex items-start gap-3 text-slate-700">
                <Check className="shrink-0 mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Analysen & Integration:</strong> Reports zu
                  Aktivitäten und Umsatzpotenzialen sowie Schnittstellen zu
                  Warenwirtschaft, Buchhaltung und Online-Systemen.
                </span>
              </li>
            </ul>
          </ImageText>
        </Section>
      }
      faqs={[
        {
          q: "Ist CRM4Trade auch für kleine Teams geeignet?",
          a: "Ja. CRM4Trade ist übersichtlich aufgebaut und eignet sich sowohl für kleine Teams als auch für wachsende Vertriebsorganisationen – der Einstieg ist schnell und ohne großen Schulungsaufwand möglich.",
        },
        {
          q: "Kann ich Aufgaben und Wiedervorlagen abbilden?",
          a: "Ja, CRM4Trade unterstützt Aufgaben, Erinnerungen und die Nachverfolgung von Kontakten und Aktivitäten – so geht kein Follow-up mehr verloren.",
        },
        {
          q: "Gibt es eine zentrale Kundenhistorie?",
          a: "Ja. Alle Kontakte, Notizen, Termine und Vorgänge können direkt beim Kunden dokumentiert und nachvollzogen werden – für einen vollständigen Überblick auf einen Blick.",
        },
        {
          q: "Lässt sich CRM4Trade mit bestehenden Systemen verbinden?",
          a: "Ja, CRM4Trade ist auf Integration ausgelegt und kann mit Warenwirtschaft, Buchhaltung oder weiteren Systemen verbunden werden – für durchgängige Prozesse ohne doppelte Datenpflege.",
        },
        {
          q: "Wie starte ich mit einer Demo?",
          a: "Einfach unverbindlich anfragen – wir melden uns zeitnah mit einem Terminvorschlag und richten die Demo individuell für Sie ein.",
        },
        {
          q: "Können mehrere Mitarbeiter gleichzeitig mit CRM4Trade arbeiten?",
          a: "Ja, CRM4Trade unterstützt den Mehrplatzbetrieb – alle Mitarbeiter arbeiten auf demselben Datenstand und sehen Änderungen in Echtzeit.",
        },
        {
          q: "Lassen sich Verkaufschancen und Pipelines verwalten?",
          a: "Ja, CRM4Trade bietet eine übersichtliche Pipeline-Ansicht für Ihre Verkaufschancen – von der ersten Kontaktaufnahme bis zum Abschluss.",
        },
        {
          q: "Wie läuft die Einrichtung ab?",
          a: "Nach einer persönlichen Beratung richtet unser Team das System für Sie ein. Schulungen und Support sind selbstverständlich inbegriffen.",
        },
      ]}
      features={[
        {
          src: ImageFeatureCustomers,
          alt: "CRM4Trade – Zentrale Kundenakte",
          title: "Zentrale Kundenakte",
          desc: "Alle relevanten Informationen an einem Ort: Ansprechpartner, Historie, Notizen, Dokumente und Kommunikation.",
        },
        {
          src: ImageFeaturePOS,
          alt: "CRM4Trade – Aufgaben und Wiedervorlagen",
          title: "Aufgaben & Wiedervorlagen",
          desc: "Strukturieren Sie Ihren Vertrieb mit Aufgaben, Wiedervorlagen und klaren Verantwortlichkeiten im Team.",
        },
        {
          src: ImageFeatureOrder,
          alt: "CRM4Trade – Lead- und Pipeline-Management",
          title: "Lead- & Pipeline-Management",
          desc: "Von der ersten Anfrage bis zum Abschluss: behalten Sie Leads, Chancen und nächste Schritte jederzeit im Blick.",
        },
        {
          src: ImageFeatureStats,
          alt: "CRM4Trade – Auswertungen und Reports",
          title: "Reports & Auswertungen",
          desc: "Treffen Sie Entscheidungen auf Basis klarer Kennzahlen – Aktivitäten, Potenziale und Team-Performance.",
        },
        {
          src: ImageFeatureStock,
          alt: "CRM4Trade – Segmentierung und Kundenfilter",
          title: "Segmentierung & Filter",
          desc: "Kunden nach Branche, Region, Umsatz oder Status segmentieren – für gezielte Kommunikation und Vertrieb.",
        },
        {
          src: ImageFeatureExport,
          alt: "CRM4Trade – Schnittstellen und Export",
          title: "Schnittstellen & Export",
          desc: "Exportieren Sie Daten oder integrieren Sie CRM4Trade in Ihre bestehende Systemlandschaft.",
        },
      ]}
    />
  );
}
